import { useId } from "react";

import { cn } from "@/lib/cn";
import type { LabRunModel } from "@/lib/labRunModel";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState } from "@/ui/states";

/**
 * Latency & models: how long each brain call took, by family and by turn, and
 * which model identity answered. Every figure is read from `model.latency`;
 * a call that recorded no latency is counted and never drawn as 0 ms. Each
 * bar carries its value as text, so length is never the only carrier.
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

function ms(value: number | null): string {
  return value === null ? "not recorded" : `${Math.round(value)} ms`;
}

function Bar({ value, max }: { value: number | null; max: number | null }) {
  const width =
    value === null || max === null || max <= 0
      ? 0
      : Math.max(2, Math.min(100, (value / max) * 100));
  return (
    <div aria-hidden="true" className="h-2 w-full rounded bg-muted">
      <div className="h-2 rounded bg-primary" style={{ width: `${width}%` }} />
    </div>
  );
}

export function LabLatencyTab({ model, selectedTurn, onSelectTurn }: Props) {
  const headingId = useId();
  const latency = model.latency;

  if (model.lab.turns.length === 0) {
    return (
      <section aria-labelledby={headingId}>
        <h2
          id={headingId}
          className="mb-3 text-base font-semibold text-foreground"
        >
          Latency &amp; models
        </h2>
        <EmptyState
          title="No turns yet"
          description="Latency appears once the run has recorded turns."
        />
      </section>
    );
  }

  const maxTurnMs = latency.byTurn.reduce<number | null>(
    (max, row) =>
      row.totalMs !== null && (max === null || row.totalMs > max)
        ? row.totalMs
        : max,
    null,
  );

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-base font-semibold text-foreground">
        Latency &amp; models
      </h2>
      <p className="text-sm text-muted-foreground">
        Token counts and cost are not recorded for brain calls. Only calls that
        were invoked and recorded a latency contribute to the figures below.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>By family</CardTitle>
        </CardHeader>
        <CardBody>
          {latency.byFamily.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No prompt calls recorded.
            </p>
          ) : (
            <ul className="space-y-3">
              {latency.byFamily.map((row) => {
                return (
                  <li key={row.family} className="space-y-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                      <span className="font-medium text-foreground">
                        {row.family}
                      </span>
                      <span className="text-muted-foreground">
                        {row.calls} {row.calls === 1 ? "call" : "calls"} · mean{" "}
                        {ms(row.meanMs)} · max {ms(row.maxMs)}
                      </span>
                    </div>
                    <Bar value={row.maxMs} max={latency.maxCallMs} />
                    {row.notInvoked > 0 || row.failed > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {row.invoked} invoked · {row.notInvoked} not invoked ·{" "}
                        {row.failed} failed
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By turn</CardTitle>
        </CardHeader>
        <CardBody>
          {latency.byTurn.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No turn recorded a call latency.
            </p>
          ) : (
            <ul className="space-y-2">
              {latency.byTurn.map((row) => (
                <li key={row.turnNumber}>
                  <button
                    type="button"
                    aria-current={
                      selectedTurn === row.turnNumber ? "true" : undefined
                    }
                    onClick={() => onSelectTurn(row.turnNumber)}
                    className={cn(
                      "w-full space-y-1 rounded-md border border-transparent px-2 py-1 text-start hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
                      selectedTurn === row.turnNumber &&
                        "border-border bg-muted",
                    )}
                  >
                    <span className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                      <span className="font-medium text-foreground">
                        Turn {row.turnNumber}
                      </span>
                      <span className="text-muted-foreground">
                        total {ms(row.totalMs)} · {row.calls}{" "}
                        {row.calls === 1 ? "call" : "calls"}
                      </span>
                    </span>
                    <Bar value={row.totalMs} max={maxTurnMs} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Models</CardTitle>
        </CardHeader>
        <CardBody>
          {latency.models.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No model identity recorded.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-start text-xs text-muted-foreground">
                    <th
                      scope="col"
                      className="py-1 pe-3 text-start font-medium"
                    >
                      Model
                    </th>
                    <th
                      scope="col"
                      className="py-1 pe-3 text-start font-medium"
                    >
                      Deployment
                    </th>
                    <th scope="col" className="py-1 text-start font-medium">
                      Calls
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {latency.models.map((row) => (
                    <tr
                      key={`${row.model ?? ""}|${row.deployment ?? ""}`}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-1 pe-3">
                        {row.model ?? "not carried"}
                      </td>
                      <td className="py-1 pe-3">
                        {row.deployment ?? "not carried"}
                      </td>
                      <td className="py-1">{row.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Not invoked</CardTitle>
        </CardHeader>
        <CardBody>
          {latency.notInvoked.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every recorded call invoked its model.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {latency.notInvoked.map((row) => (
                <li key={`${row.family}|${row.reasonCode ?? ""}`}>
                  <span className="font-medium text-foreground">
                    {row.family}
                  </span>
                  {" · "}
                  {row.reasonCode ?? "no reason recorded"} ×{row.count}
                  {row.label ? (
                    <span className="text-muted-foreground">
                      {" "}
                      — {row.label}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {model.sim !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>Patient simulator</CardTitle>
          </CardHeader>
          <CardBody>
            {latency.sim.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                The simulator recorded no per-turn stats.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs text-muted-foreground">
                      <th
                        scope="col"
                        className="py-1 pe-3 text-start font-medium"
                      >
                        Turn
                      </th>
                      <th
                        scope="col"
                        className="py-1 pe-3 text-start font-medium"
                      >
                        Latency
                      </th>
                      <th scope="col" className="py-1 text-start font-medium">
                        Model
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {latency.sim.map((row) => (
                      <tr
                        key={row.turn}
                        className="border-b border-border last:border-0"
                      >
                        <td className="py-1 pe-3">{row.turn}</td>
                        <td className="py-1 pe-3">{ms(row.latencyMs)}</td>
                        <td className="py-1">{row.model}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}
    </section>
  );
}
